import { ContentCopy, Inventory } from "@mui/icons-material";
import { FC, MouseEventHandler } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { PIC, SVG_MUI } from "@ryde/assets";
import { AccessControl, MuiButton } from "@ryde/controls";
import { useJourneysEdit, useModalConfirm } from "@ryde/redux";
import { ConfirmResult, RydeUiUnit } from "@ryde/shared";

import { EditFormFooterButton } from "../../../right";
import { CrudEditFormFooterStyled } from "../drawer/crud-edit-form-footer.styled";

interface IJourneyCrudEditFormFooterProps {
  isEdit?: boolean;
  entity: RydeUiUnit;
  saveLabel?: string;
  isSaveDisabled?: boolean;
  cancelLabel?: string;
  isLoading?: boolean;
  onSaveClick?: () => void;
  onCancelClick: () => void;
  archiveClick?: MouseEventHandler<HTMLElement>;
}

export const JourneyCrudEditFormFooter: FC<IJourneyCrudEditFormFooterProps> = ({
  isEdit,
  entity,
  saveLabel,
  cancelLabel,
  isLoading = false,
  isSaveDisabled = false,
  onSaveClick,
  onCancelClick,
  archiveClick,
}) => {
  const { confirmDialogResult } = useModalConfirm();

  const {
    ridesJourney,
    onAddRideClick,
    onRidesNavigationClick,
    onJourneyDuplicateClick,
  } = useJourneysEdit();
  const { t } = useTranslation();

  const unitTitle = t(`${entity}.single`);

  const onArchiveClickEvent = useEvent(
    async (e: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
      e.preventDefault();

      if (archiveClick) {
        const confirmationResult = await confirmDialogResult({
          title: t("general.ARCHIVE_CONFIRM"),
          message: t(`general.ARCHIVE_MESSAGE`, { entity: unitTitle }),
        });

        if (confirmationResult === ConfirmResult.OK) {
          archiveClick(e);
        }
      }
    },
  );

  const onDuplicateClickEvent = useEvent(
    async (e: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
      e.preventDefault();

      if (isEdit) {
        onJourneyDuplicateClick();
      }
    },
  );

  return (
    <CrudEditFormFooterStyled>
      {isEdit && (
        <AccessControl entity={entity || "unknown"} action="delete">
          <EditFormFooterButton
            onClick={onArchiveClickEvent}
            disabled={!isEdit || isLoading}
          >
            <Inventory style={{ fontSize: "16px" }} />
            <span>{`${t(`general.ARCHIVE`)}  ${unitTitle}`}</span>
          </EditFormFooterButton>
        </AccessControl>
      )}

      {/* DUPLICATE JOURNEY */}
      {isEdit && (
        <AccessControl entity={entity || "unknown"} action="create">
          <EditFormFooterButton
            onClick={onDuplicateClickEvent}
            disabled={isLoading}
          >
            <ContentCopy style={{ fontSize: "16px" }} />
            <span>{`${t(`general.DUPLICATE`)}  ${unitTitle}`}</span>
          </EditFormFooterButton>
        </AccessControl>
      )}

      {/* ADD RIDE */}
      {isEdit && (
        <AccessControl entity={"ride"} action="create">
          <EditFormFooterButton onClick={onAddRideClick} disabled={isLoading}>
            <PIC.PlusSvg />
            <span>{`${t(`general.APPEND`)} ${t(`ride.single`)}`}</span>
          </EditFormFooterButton>
        </AccessControl>
      )}

      {/* NAVIGATE TO RIDES */}
      {Boolean(ridesJourney?.length) && (
        <AccessControl entity={"ride"} action="read">
          <EditFormFooterButton
            onClick={onRidesNavigationClick}
            disabled={isLoading}
          >
            <SVG_MUI.ArrowUpRightFromSquareSVG style={{ fontSize: "16px" }} />
            <span>{`${t(`ride.openInRides`)}`}</span>
          </EditFormFooterButton>
        </AccessControl>
      )}

      {/* Cancel Button */}
      <MuiButton
        className="cancel_button"
        e2eData={`cancel_button_right_footer`}
        muiVariant="outlined"
        color="secondary"
        onClick={onCancelClick}
        loading={isLoading}
      >
        {cancelLabel || t(`general.CANCEL`)}
      </MuiButton>

      {/* Save Button */}
      {Boolean(onSaveClick) && (
        <MuiButton
          e2eData={`save_button_right_footer`}
          loading={isLoading}
          muiStyle={isSaveDisabled ? "error" : "primary"}
          disabled={isSaveDisabled}
          onClick={onSaveClick}
        >
          {saveLabel || t(`general.SAVE`)}
        </MuiButton>
      )}
    </CrudEditFormFooterStyled>
  );
};
